/**
 * Email OTP UI: code TTL label + resend button cooldown.
 * Intervals are stored on ``window`` so ``30-router.shell.js`` can clear them on navigation.
 */

/** @param {number} totalSeconds */
export function formatOtpCountdownMmSs(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${String(sec).padStart(2, "0")}s`;
}

/**
 * @param {HTMLElement | null} labelEl
 * @param {number} ttlSeconds
 * @param {{ expiredMessage?: string }} [opts]
 */
export function startEmailCodeExpiryCountdown(labelEl, ttlSeconds, opts = {}) {
  const expiredMessage =
    opts.expiredMessage || "Verification failed — request a new code";
  if (window.__authEmailOtpInterval) {
    try {
      clearInterval(window.__authEmailOtpInterval);
    } catch (_) {}
    window.__authEmailOtpInterval = 0;
  }
  if (!labelEl) return () => {};
  let left = Math.max(0, Math.floor(Number(ttlSeconds) || 0));
  if (left <= 0) {
    labelEl.textContent = expiredMessage;
    return () => {};
  }
  const apply = () => {
    if (!labelEl.isConnected) {
      if (window.__authEmailOtpInterval) clearInterval(window.__authEmailOtpInterval);
      window.__authEmailOtpInterval = 0;
      return;
    }
    if (left <= 0) {
      labelEl.textContent = expiredMessage;
      if (window.__authEmailOtpInterval) clearInterval(window.__authEmailOtpInterval);
      window.__authEmailOtpInterval = 0;
      return;
    }
    labelEl.textContent = `Code expires in ${formatOtpCountdownMmSs(left)}`;
    left -= 1;
  };
  apply();
  window.__authEmailOtpInterval = setInterval(apply, 1000);
  return () => {
    if (window.__authEmailOtpInterval) {
      clearInterval(window.__authEmailOtpInterval);
      window.__authEmailOtpInterval = 0;
    }
  };
}

/**
 * @param {HTMLButtonElement | null} buttonEl
 * @param {number} seconds
 * @param {string} idleLabel button text when ready
 */
export function startAuthResendCooldown(buttonEl, seconds, idleLabel) {
  if (window.__authResendCooldownInterval) {
    try {
      clearInterval(window.__authResendCooldownInterval);
    } catch (_) {}
    window.__authResendCooldownInterval = 0;
  }
  if (!buttonEl) return () => {};
  const base = idleLabel || buttonEl.textContent || "Resend";
  let s = Math.max(0, Math.floor(Number(seconds) || 0));
  const tick = () => {
    if (!buttonEl.isConnected) {
      if (window.__authResendCooldownInterval) clearInterval(window.__authResendCooldownInterval);
      window.__authResendCooldownInterval = 0;
      return;
    }
    if (s <= 0) {
      buttonEl.disabled = false;
      buttonEl.textContent = base;
      if (window.__authResendCooldownInterval) clearInterval(window.__authResendCooldownInterval);
      window.__authResendCooldownInterval = 0;
      return;
    }
    buttonEl.disabled = true;
    buttonEl.textContent = `Resend in ${formatOtpCountdownMmSs(s)}`;
    s -= 1;
  };
  tick();
  if (s > 0) window.__authResendCooldownInterval = setInterval(tick, 1000);
  return () => {
    if (window.__authResendCooldownInterval) {
      clearInterval(window.__authResendCooldownInterval);
      window.__authResendCooldownInterval = 0;
    }
    try {
      buttonEl.disabled = false;
      buttonEl.textContent = base;
    } catch (_) {}
  };
}
